import { useEffect, useRef, useState } from "react";
import { Alert, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { MonitorButton } from "../../components/MonitorControls";
import { api } from "../../utils/helpers";

// This control assigns existing verified runs. It never starts another sync.
export default function EdgeRecovery({ spaceId, instanceId, packageId, runId }) {
  const { t } = useTranslation("freigabe");
  const [plans, setPlans] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const alive = useRef(true);
  const inFlight = useRef(false);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  if (!spaceId || !instanceId || !packageId || !runId) return null;
  const scope = { spaceId, instanceId, packageId, runId: String(runId) };
  async function check() {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true); setMessage(null);
    try {
      const data = await api(`/api/cicd/transport/edge-recovery?${new URLSearchParams(scope)}`, { retry: false, cache: "no-store" });
      if (!data || !Array.isArray(data.plans) || data.plans.some(plan => !plan || typeof plan.id !== "string"
          || typeof plan.revision !== "string" || !Array.isArray(plan.repairs) || !plan.repairs.length
          || plan.repairs.some(item => !item || !Array.isArray(item.packageIds) || !item.packageIds.length
            || item.packageIds.some(id => typeof id !== "string" || !id) || ["pid", "fromRuntimeId", "runtimeId", "operationId"].some(key => typeof item[key] !== "string" || !item[key])))) throw Error("EDGE_RECOVERY_UNAVAILABLE");
      if (alive.current) { setPlans(data.plans); if (!data.plans.length) setMessage("none"); }
    } catch { if (alive.current) { setPlans(null); setMessage("failed"); } }
    finally { inFlight.current = false; if (alive.current) setBusy(false); }
  }
  async function accept(plan) {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true); setMessage(null);
    try {
      const data = await api("/api/cicd/transport/edge-recovery", { method: "POST", retry: false,
        body: JSON.stringify({ ...scope, ...plan }) });
      if (!data || !["completed", "partial"].includes(data.status)) throw Error("EDGE_RECOVERY_UNAVAILABLE");
      if (alive.current) {
        setPlans(previous => previous.filter(item => item.id !== plan.id));
        setMessage(data.status === "completed" ? "completed" : "partial");
      }
    } catch { if (alive.current) { setPlans(null); setMessage("failed"); } }
    finally { inFlight.current = false; if (alive.current) setBusy(false); }
  }
  return <Stack spacing={1} sx={{ mb: 2, minWidth: 0 }}>
    {message && <Alert severity={message === "failed" ? "warning" : "info"}>{t(`edgeRecovery.${message}`)}</Alert>}
    {plans?.map(plan => <Stack key={plan.id} spacing={1}>
      <Typography variant="body2">{t("edgeRecovery.confirm")}</Typography>
      {plan.repairs.map(item => <Typography variant="body2" key={item.pid} sx={{ overflowWrap: "anywhere" }}>
        {item.pid}: {item.fromRuntimeId} → {item.runtimeId}<br/>{t("edgeRecovery.packages", { packages: item.packageIds.join(", ") })}
      </Typography>)}
      <MonitorButton disabled={busy} onClick={() => accept(plan)}>{t("edgeRecovery.accept")}</MonitorButton>
    </Stack>)}
    {!plans?.length && <MonitorButton disabled={busy} onClick={check}>{t(busy ? "edgeRecovery.checking" : "edgeRecovery.check")}</MonitorButton>}
  </Stack>;
}
